import { listAudit } from "@/api/admin";
import { useAsync } from "@/lib/useAsync";
import { when } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { PagedList, usePagedCursor } from "@/components/ui/paged-list";
import { TD, TH, TR } from "@/components/ui/table";

/**
 * Who did what, and to which row.
 *
 * THE DIFF IS WHAT CHANGED, NOT THE WHOLE ROW. The worker records only the
 * fields that moved, deliberately: an audit trail that copied every column
 * would be a second database with none of the first one's constraints, holding
 * customer emails long after the retention sweep removed the originals.
 *
 * WHO, BY NAME, falling back to the id. This said that `admin_id` was shown
 * "rather than an email because `admin_audit` carries no foreign key to
 * `admin_users` ... so there is deliberately nothing to join to", and it was
 * wrong twice over: `listAudit` LEFT JOINs `admin_users` precisely to produce
 * `admin_email`, and no foreign key is the REASON a LEFT JOIN is the right
 * shape rather than a reason not to join. The trail outliving the account is
 * what the null branch is for.
 *
 * And the column was not there at all -- neither the email nor the id. An
 * audit trail whose rows do not say who did the thing answers the one question
 * it exists to answer with "somebody".
 */
export function AuditScreen() {
  const pager = usePagedCursor("audit");
  const { data, loading, error } = useAsync(() => listAudit({ cursor: pager.cursor }), [pager.cursor]);
  const rows = data?.rows ?? [];

  return (
    <div className="space-y-4">
      <PageHeader title="Audit trail" description="Every administrator write, with the change it made." />
      <PagedList
        columns={6}
        rows={rows}
        loading={loading}
        error={error}
        empty="Nothing has been changed yet."
        pager={pager}
        nextCursor={data?.nextCursor}
        head={
          <>
            <TH>When</TH>
            <TH>Who</TH>
            <TH>Action</TH>
            <TH>Thing</TH>
            <TH>Change</TH>
            <TH>From</TH>
          </>
        }
        renderRow={(entry) => (
          <TR key={entry.id}>
            <TD className="whitespace-nowrap text-muted-foreground">{when(entry.created_at)}</TD>
            <TD className="whitespace-nowrap">
              {entry.admin_email ?? (
                // The account is gone, which the trail is built to survive.
                // The id is still evidence, and saying so beats a dash.
                <span className="font-mono text-xs text-muted-foreground" title={entry.admin_id}>
                  deleted account
                </span>
              )}
            </TD>
            <TD>
              <Badge color={entry.action.includes("delete") || entry.action.includes("revoke") ? "red" : "blue"}>
                {entry.action}
              </Badge>
            </TD>
            <TD className="font-mono text-xs">
              {entry.entity_kind ? `${entry.entity_kind}:${entry.entity_id ?? ""}` : "—"}
            </TD>
            <TD className="max-w-[320px]">
              {entry.diff_json ? (
                // Pre-wrapped rather than truncated: the diff is the reason
                // anybody opens this screen, and an ellipsis in the middle of
                // "slugFrom" is the half nobody needed.
                <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-snug text-secondary">
                  {entry.diff_json}
                </pre>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TD>
            <TD className="whitespace-nowrap font-mono text-xs text-muted-foreground">{entry.ip ?? "—"}</TD>
          </TR>
        )}
      />
    </div>
  );
}
